import * as React from 'react';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import classNames from 'classnames';
import { fetchItemsIfNeeded } from '@/redux/modules/item';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import ItemCard from '@/components/ItemCard/ItemCard';
import ItemGrid from '@/components/ItemCard/ItemGrid';
import noop from '@/utils/noop';
import SeeAllLink from '../SeeAllLink/SeeAllLink';
import SideScroller from '@/components/SideScroller/SideScroller';
import styles from './ItemShelf.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useDispatch } from 'react-redux';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const GRID_CHANGE_WIDTH = 550;

type Props = {
    allowBidding?: boolean;
    className: string;
    cta?: boolean;
    hideLiveButton?: boolean;
    identifier: string;
    itemIds: number[];
    links?: React.ReactNode;
    seeAllText?: React.ReactNode;
    seeAllUrl?: string;
    showBidStatus?: boolean;
    showSoldBadge?: boolean;
    title: React.ReactNode;
    trackItemClick?: Function;
};
const ItemShelf = (props: Props) => {
    const dispatch = useDispatch();
    const {
        allowBidding,
        className = '',
        cta = false,
        hideLiveButton,
        identifier,
        itemIds = [],
        links = false,
        seeAllText = '',
        seeAllUrl = '',
        showBidStatus = false,
        showSoldBadge = false,
        title,
        trackItemClick = noop,
    } = props;
    const wide = useIsWiderThan(GRID_CHANGE_WIDTH);
    useDeepCompareEffect(() => {
        if (itemIds.length) {
            dispatch(fetchItemsIfNeeded(itemIds, identifier));
        }
    }, [dispatch, itemIds, identifier]);
    const itemsString = itemIds.join(',');
    const trackClick = React.useCallback(
        (itemId) => {
            trackItemClick(itemId);
        },
        [trackItemClick]
    );
    const cards = React.useMemo(() => {
        if (itemsString) {
            return itemsString.split(',').map((itemId: string, itemIndex: number) => {
                const key = `${itemIndex}-${identifier}`;
                return (
                    <ItemCard
                        allowBidding={allowBidding}
                        className={styles.item}
                        hideLiveButton={hideLiveButton}
                        hideLotNumbers
                        hideSeller
                        itemId={Number(itemId)}
                        key={key}
                        noWidthLimit
                        showBidStatus={showBidStatus}
                        showSoldBadge={showSoldBadge}
                        trackClick={() => trackClick(itemId)}
                    />
                );
            });
        }
        return [];
    }, [allowBidding, hideLiveButton, identifier, itemsString, showBidStatus, showSoldBadge, trackClick]);
    let content;
    let classes = classNames(styles.container, className);
    if (itemIds.length) {
        content = (
            <>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <h2>{title}</h2>
                        {wide && Boolean(seeAllUrl) && <SeeAllLink text={seeAllText} url={seeAllUrl} />}
                    </div>
                </div>
                {Boolean(links) && <div className={styles.links}>{links}</div>}
                {wide ? (
                    <SideScroller className={styles.carousel}>{cards}</SideScroller>
                ) : (
                    <>
                        <ItemGrid
                            className={styles.grid}
                            hideLotNumbers
                            hideSeller
                            itemCardClass={styles.card}
                            itemIds={itemIds.slice(0, 6)}
                            showBidStatusOnCards
                            showSoldBadge
                        />
                        {cta && (
                            <div className={styles.cta}>
                                <ButtonLink url={seeAllUrl}>
                                    <FormattedMessage id="see_all" />
                                </ButtonLink>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    } else {
        classes = classNames(styles.container, className, styles.wait);
        content = <Throbber />;
    }
    return <div id={identifier}>{Boolean(itemIds.length) && <div className={classes}>{content}</div>}</div>;
};
export default React.memo<Props>(ItemShelf, isEqual);
